import type { DrawingDocument, DrawingLine, GraphEdge, NormalizedPoint, Point2D } from '../model';
import { aromaticBondIds, isChemicalBond } from './documentSelectors.ts';
import { centroidOf } from './haptoCentroid.ts';
import { maximumSupportedValence } from '../chemistry/chemicalValidation.ts';

export interface DrawingAtom {
  readonly id: string;
  readonly position: NormalizedPoint;
  readonly atomicNumber: number;
  readonly formalCharge: number;
  readonly implicitHydrogenCount: number;
  readonly implicitHydrogenDirection?: Readonly<{ x: number; y: number }>;
  readonly isotope?: number;
  readonly radicalElectronCount?: number;
  readonly resonanceCharge?: number;
  // #576: true when this atom's current bond-order sum is below its maximum supported valence
  // (chemicalValidation's own MAX_VALENCE_BY_ATOMIC_NUMBER ceiling) -- "can accept a new bond" in
  // the drawing-affordance sense, not the medicinal-chemistry hydrogen-bond-acceptor concept.
  // Deliberately NOT implicitHydrogenCount below: that field's own defaultValenceFor (this file)
  // only special-cases the nitrogen and oxygen groups for *label* purposes (whether to render an explicit "H"
  // count), so it's always 0 for carbon even though a carbon vertex with 3 bonds plainly has room
  // for a 4th. Undefined ceiling (an element the table doesn't cover) reads as "not open" -- an
  // unknown ceiling should never be claimed as available.
  readonly hasOpenValence: boolean;
  // #648: display text drawn in place of the element symbol -- a Markush R-group label (`R1`) or
  // atom list (`[C,N,O,S]`). Set only by the workspace's Markush mode; the atom stays a normal
  // atom for every tool.
  readonly labelOverride?: string;
  // #1068: true when no bond touches this atom. A bare carbon has no lines to imply it, so it
  // draws its symbol (CH4) instead of vanishing while the formula still counts it.
  readonly isolated?: true;
}

/** #648: draws `overrides[atom.id]` in place of the element symbol (Markush R labels). An
 *  overridden atom shows no implicit hydrogens -- an R label stands for the whole substituent. */
export function withAtomLabelOverrides(
  atoms: readonly DrawingAtom[],
  overrides: Readonly<Record<string, string>>
): readonly DrawingAtom[] {
  if (Object.keys(overrides).length === 0) return atoms;
  return atoms.map((atom) => {
    const labelOverride = overrides[atom.id];
    if (labelOverride === undefined) return atom;
    const { implicitHydrogenDirection: _direction, ...rest } = atom;
    return { ...rest, implicitHydrogenCount: 0, labelOverride };
  });
}

/** ADR 0026 D6: the carbon an agostic interaction's hydrogen is bonded to, if any. */
export function agosticCarbon(document: DrawingDocument, hydrogenId: string): string | undefined {
  const carbonEdge = document.structure.graph.edges.find((edge) => {
    if (edge.source !== hydrogenId && edge.target !== hydrogenId) return false;
    if (!isChemicalBond(document.structure.bonds[edge.id])) return false;
    const other = edge.source === hydrogenId ? edge.target : edge.source;
    return document.structure.atoms[other]?.atomicNumber === 6;
  });
  return carbonEdge && (carbonEdge.source === hydrogenId ? carbonEdge.target : carbonEdge.source);
}

/** ADR 0026 D6: each agostic interaction's hydrogen and carbon, by bond id -- it starts between them. */
export function agosticStarts(document: DrawingDocument): Readonly<Record<string, readonly [string, string]>> {
  return Object.fromEntries(
    document.structure.graph.edges.flatMap((edge) => {
      if (document.structure.bonds[edge.id]?.type !== 'agostic') return [];
      const carbon = agosticCarbon(document, edge.source);
      return carbon ? [[edge.id, [edge.source, carbon] as const]] : [];
    })
  );
}

/** ADR 0026 D6: where an agostic interaction starts -- the middle of the H's bond to its carbon,
 *  or the H itself when it has none. */
export function agosticStart(document: DrawingDocument, hydrogenId: string): Point2D | undefined {
  const { coordinates } = document.depiction.coordinateSet;
  const hydrogen = coordinates[hydrogenId];
  const carbonId = agosticCarbon(document, hydrogenId);
  const carbon = carbonId ? coordinates[carbonId] : undefined;
  return hydrogen && carbon ? { x: (hydrogen.x + carbon.x) / 2, y: (hydrogen.y + carbon.y) / 2 } : hydrogen;
}

export function documentToLines(document: DrawingDocument): readonly DrawingLine[] {
  const coordinateSet = document.depiction.coordinateSet;
  return document.structure.graph.edges.flatMap((edge) => {
    // ADR 0026 D6: an agostic interaction is drawn from the middle of its C–H bond.
    const start =
      document.structure.bonds[edge.id]?.type === 'agostic'
        ? agosticStart(document, edge.source)
        : coordinateSet.coordinates[edge.source];
    // ADR 0026 D2: a hapto bond is drawn from the metal to the middle of its ligand atoms.
    const ligand = document.structure.bonds[edge.id]?.ligandAtomIds;
    const end = ligand
      ? centroidOf(ligand, (id) => coordinateSet.coordinates[id])
      : coordinateSet.coordinates[edge.target];
    if (!start || !end) return [];
    return [
      {
        id: edge.id,
        pointerType: 'unknown' as const,
        createdAt: '',
        start: { x: start.x / coordinateSet.width, y: start.y / coordinateSet.height },
        end: { x: end.x / coordinateSet.width, y: end.y / coordinateSet.height }
      }
    ];
  });
}

function otherEndpoint(edge: GraphEdge, nodeId: string): string | undefined {
  if (edge.source === nodeId) return edge.target;
  if (edge.target === nodeId) return edge.source;
  return undefined;
}

function aromaticBondValues(document: DrawingDocument): Readonly<Record<string, { readonly aromatic: true }>> {
  return Object.fromEntries([...aromaticBondIds(document)].map((id) => [id, { aromatic: true as const }]));
}

function findSmallestAromaticRingPath(
  edge: GraphEdge,
  aromaticEdges: readonly GraphEdge[]
): readonly string[] | undefined {
  const queue: string[][] = [[edge.target]];
  while (queue.length > 0) {
    const candidate = queue.shift()!;
    const current = candidate.at(-1)!;
    if (current === edge.source) return candidate;
    for (const neighborEdge of aromaticEdges) {
      if (neighborEdge.id === edge.id) continue;
      const neighbor = otherEndpoint(neighborEdge, current);
      if (neighbor && !candidate.includes(neighbor)) queue.push([...candidate, neighbor]);
    }
  }
  return undefined;
}

/** Every aromatic ring's atom path, keyed by one representative edge id on that ring. */
export function aromaticRingAtomPaths(document: DrawingDocument): Readonly<Record<string, readonly string[]>> {
  const aromaticEdgeIds = new Set(Object.keys(aromaticBondValues(document)));
  const aromaticEdges = document.structure.graph.edges.filter((edge) => aromaticEdgeIds.has(edge.id));
  return Object.fromEntries(
    aromaticEdges.flatMap((edge) => {
      const path = findSmallestAromaticRingPath(edge, aromaticEdges);
      return path && path.length >= 3 ? [[edge.id, path]] : [];
    })
  );
}

export function aromaticRingCenters(document: DrawingDocument): Readonly<Record<string, NormalizedPoint>> {
  const coordinateSet = document.depiction.coordinateSet;
  return Object.fromEntries(
    Object.entries(aromaticRingAtomPaths(document)).flatMap(([edgeId, path]) => {
      const coordinates: readonly (Point2D | undefined)[] = path.map((nodeId) => coordinateSet.coordinates[nodeId]);
      if (coordinates.includes(undefined)) return [];
      return [
        [
          edgeId,
          {
            x: coordinates.reduce((sum, point) => sum + point!.x, 0) / coordinates.length / coordinateSet.width,
            y: coordinates.reduce((sum, point) => sum + point!.y, 0) / coordinates.length / coordinateSet.height
          }
        ]
      ];
    })
  );
}

// #671: an element takes its group's hydrogens -- sulfur, selenium and tellurium as oxygen (a
// terminal S is SH, as O is OH), phosphorus and arsenic as nitrogen. Their higher valences
// (S in DMSO, P in phosphates) need no hydrogens, so the lowest one is the rule.
const PNICTOGENS = new Set([7, 15, 33]);
const CHALCOGENS = new Set([8, 16, 34, 52]);

function defaultValenceFor(atomicNumber: number, formalCharge: number): number {
  // They also carry implicit hydrogens when charged: a cation raises the valence (N+ -> 4,
  // O+ -> 3), an anion lowers it (N- -> 2, O- -> 1). Rule 4.4.1's auto charge on an over-valent
  // N / O then leaves 0 implicit H, which is what a 4-bonded ammonium should show.
  if (PNICTOGENS.has(atomicNumber)) return Math.max(0, 3 + formalCharge);
  if (CHALCOGENS.has(atomicNumber)) return Math.max(0, 2 + formalCharge);
  return 0;
}

export function documentToAtoms(document: DrawingDocument): readonly DrawingAtom[] {
  const coordinateSet = document.depiction.coordinateSet;
  const bondOrderByNodeId: Record<string, number> = Object.fromEntries(
    document.structure.graph.nodes.map((node) => [node.id, 0])
  );
  // #1037: implicit hydrogens see a dative bond in its dipolar form -- a full bond on both atoms,
  // +1 on the donor and -1 on the acceptor (N->O is N+-O-), as RDKit's input and the server do.
  // Counting it on the acceptor only gave N->O's oxygen a stray H.
  const hydrogenBondOrderByNodeId: Record<string, number> = { ...bondOrderByNodeId };
  const dativeChargeByNodeId: Record<string, number> = {};
  const bondedNodeIds = new Set<string>();
  for (const edge of document.structure.graph.edges) {
    const bond = document.structure.bonds[edge.id];
    // ADR 0022: an atom held only by a hydrogen bond is still a lone atom chemically.
    if (isChemicalBond(bond)) {
      bondedNodeIds.add(edge.source);
      bondedNodeIds.add(edge.target);
    }
    const order = bond?.order ?? 1;
    // A dative bond counts toward the acceptor's valence only -- the donor spends a lone pair
    // (RDKit BondType.DATIVE; B4 #273).
    const donor = bond?.type === 'dative' ? bond.donorAtomId : undefined;
    if (edge.source !== donor) bondOrderByNodeId[edge.source] = (bondOrderByNodeId[edge.source] ?? 0) + order;
    if (edge.target !== donor) bondOrderByNodeId[edge.target] = (bondOrderByNodeId[edge.target] ?? 0) + order;
    hydrogenBondOrderByNodeId[edge.source] = (hydrogenBondOrderByNodeId[edge.source] ?? 0) + order;
    hydrogenBondOrderByNodeId[edge.target] = (hydrogenBondOrderByNodeId[edge.target] ?? 0) + order;
    if (donor !== undefined) {
      const acceptor = donor === edge.source ? edge.target : edge.source;
      dativeChargeByNodeId[donor] = (dativeChargeByNodeId[donor] ?? 0) + 1;
      dativeChargeByNodeId[acceptor] = (dativeChargeByNodeId[acceptor] ?? 0) - 1;
    }
  }
  return document.structure.graph.nodes.flatMap((node) => {
    const coordinate = coordinateSet.coordinates[node.id];
    const atom = document.structure.atoms[node.id];
    const charge = document.structure.formalCharges[node.id];
    if (!coordinate || !atom || charge === undefined) return [];
    const isolated = !bondedNodeIds.has(node.id);
    // #1068: carbon's hydrogens are otherwise implied by its bonds and never drawn; a lone neutral
    // carbon shows all four (CH4), as RDKit counts them in the formula.
    const defaultValence =
      isolated && atom.atomicNumber === 6 && charge === 0 && !atom.radicalElectronCount
        ? 4
        : defaultValenceFor(atom.atomicNumber, charge + (dativeChargeByNodeId[node.id] ?? 0));
    const bondOrderSum = bondOrderByNodeId[node.id] ?? 0;
    const implicitHydrogenCount = Math.max(0, defaultValence - (hydrogenBondOrderByNodeId[node.id] ?? 0));
    const maxValence = maximumSupportedValence(atom.atomicNumber, charge);
    const hasOpenValence = maxValence !== undefined && bondOrderSum < maxValence;
    const neighborCoordinates =
      implicitHydrogenCount > 0
        ? document.structure.graph.edges.flatMap((edge) => {
            if (edge.source !== node.id && edge.target !== node.id) return [];
            const neighborId = edge.source === node.id ? edge.target : edge.source;
            const neighborCoordinate = coordinateSet.coordinates[neighborId];
            return neighborCoordinate ? [neighborCoordinate] : [];
          })
        : [];
    const neighborCenterX =
      neighborCoordinates.length > 0
        ? neighborCoordinates.reduce((sum, neighbor) => sum + neighbor.x, 0) / neighborCoordinates.length
        : undefined;
    const directionOverride = document.depiction.atomLabels[node.id]?.hydrogenDirection;
    // With no neighbours to point away from, the hydrogens go where formulas write them: before an
    // O-group symbol (H2O, H2S), after the rest (CH4, NH3).
    const implicitHydrogenDirection =
      directionOverride ??
      (neighborCenterX !== undefined
        ? { x: coordinate.x < neighborCenterX ? -1 : 1, y: 0 }
        : isolated && implicitHydrogenCount > 0
          ? { x: CHALCOGENS.has(atom.atomicNumber) ? -1 : 1, y: 0 }
          : undefined);
    return [
      {
        id: node.id,
        position: {
          x: coordinate.x / coordinateSet.width,
          y: coordinate.y / coordinateSet.height
        },
        atomicNumber: atom.atomicNumber,
        formalCharge: charge,
        implicitHydrogenCount,
        hasOpenValence,
        ...(isolated ? { isolated } : {}),
        ...(implicitHydrogenDirection ? { implicitHydrogenDirection } : {}),
        ...(atom.isotope ? { isotope: atom.isotope } : {}),
        ...(atom.radicalElectronCount ? { radicalElectronCount: atom.radicalElectronCount } : {}),
        ...(document.depiction.atomLabels[node.id]?.resonanceCharge !== undefined
          ? { resonanceCharge: document.depiction.atomLabels[node.id]!.resonanceCharge }
          : {})
      }
    ];
  });
}
